import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import FabBar from "@/design/components/FabBar";
import MasterListVirtualized from "@/design/components/MasterListVirtualized";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viPlus, viStore } from "@sippy-platform/valkyrie";

import { useMediaQuery, useTheme } from "@mui/material";
import Button from "@mui/material/Button";

function PartnerList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const partnerStore = useSelector((state) => state.backstage.partners);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  // Search every time partnerList or searchQuery changes
  const searchResults = useSearch(partnerStore.list, searchValue, ["name"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.resolve(Api.getPartners()).finally(toggleLoadingState);
  }, []);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp && partnerStore.meta.lastFetched) {
      if (partnerStore.list.length >= 1) {
        navigate(`${partnerStore.list[0].id}`);
      } else {
        navigate("new");
      }
    }
  }, [id, matchesMdUp, navigate, partnerStore.list, partnerStore.meta.lastFetched]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterListVirtualized
        fabBar
        title="Partners"
        aria-label="Partner list"
        id="partner-list"
        list={searchResults}
        listItemProps={{
          primary: (x) => x.name,
          secondary: (x) => `${x.pbxCount ?? 0} ${x.pbxCount === 1 ? "PBX" : "PBXes"}`,
          to: (x) => `../${x.id}`,
          selected: (x) => x.id === Number(id),
          icon: viStore,
        }}
        hideContentLoader={partnerStore.meta.count >= 1 && searchResults.length >= 1}
        contentLoaderProps={{
          count: partnerStore.meta.count,
          loading: progressBar,
          noItems: "You don't have any partners yet",
          noResults: "No partners in your current query",
          fetching: "Getting your partners",
          icon: viStore,
        }}
      />
      <FabBar>
        <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </>
  );
}

export default PartnerList;
